import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { markRequest } from '../lib/actionFailureSignals';
import { revisionedRead } from './readCache';

type ClientOption = { supabase?: SupabaseClient };

export type LogisticsOriginLocation = {
  location_id: string; name: string; display_name: string | null;
  address_line1: string | null; address_line2: string | null; city: string | null;
  state: string | null; postal_code: string | null; country: string | null;
};

export const getLogisticsOriginQueryOptions = (locationId: string | null) => ({
  queryKey: ['event-detail-service-origin-location', locationId],
  enabled: Boolean(locationId),
  staleTime: 60_000,
  queryFn: async (): Promise<LogisticsOriginLocation | null> => {
    if (!locationId) return null;
    const { data, error } = await getSupabaseClient().from('locations')
      .select('location_id,name,display_name,address_line1,address_line2,city,state,postal_code,country')
      .eq('location_id', locationId).maybeSingle();
    if (error) throw error;
    return data;
  },
});

export type LocationRow = {
  location_id: string;
  name: string;
  region_id?: string | null;
  timezone?: string | null;
};
export type DocSequenceRow = { location_id: string | null; prefix?: string | null; doc_type?: string | null };

export async function fetchLocationTimezones(
  locationIds: string[],
  options?: ClientOption,
): Promise<{ data: Record<string, string | null>; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const ids = Array.from(new Set(
    (Array.isArray(locationIds) ? locationIds : [])
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean),
  ));
  if (ids.length === 0) return { data: {}, error: null };

  const { data, error } = await supabase
    .from('locations')
    .select('location_id,timezone')
    .in('location_id', ids);
  if (error) return { data: {}, error };

  const timezones: Record<string, string | null> = {};
  ids.forEach((id) => {
    timezones[id] = null;
  });
  (Array.isArray(data) ? data : []).forEach((row) => {
    const id = typeof row?.location_id === 'string' ? row.location_id.trim() : '';
    if (!id) return;
    const timezone = typeof row?.timezone === 'string' ? row.timezone.trim() : '';
    timezones[id] = timezone || null;
  });
  return { data: timezones, error: null };
}

export const getLocationTimezonesQueryOptions = ({
  locationIds,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  locationIds: Array<string | null | undefined>;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => {
  const normalized = Array.from(new Set(
    locationIds
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean),
  ));
  return {
    queryKey: ['location-timezones', ...normalized.slice().sort()],
    enabled: enabled && normalized.length > 0,
    queryFn: async () => {
      const { data, error } = await fetchLocationTimezones(normalized, { supabase });
      if (error) throw error;
      return data;
    },
  };
};

export async function fetchLocationPages({
  supabase = getSupabaseClient(), locationIds, regionIds, activeOnly = true,
}: ClientOption & { locationIds?: string[] | null; regionIds?: string[]; activeOnly?: boolean } = {}): Promise<{ data: LocationRow[]; error: PostgrestError | null }> {
  const ids = Array.from(new Set((locationIds ?? []).map(id => id.trim()).filter(Boolean)));
  const groups: Array<string[] | null> = ids.length
    ? Array.from({ length: Math.ceil(ids.length / 500) }, (_, index) => ids.slice(index * 500, (index + 1) * 500))
    : [null];
  const locations: LocationRow[] = [];
  for (const group of groups) {
    let after: string | null = null;
    while (true) {
      const { data, error } = await supabase.rpc('get_internal_location_page', {
        p_location_ids: group, p_region_ids: regionIds?.length ? regionIds : null,
        p_active_only: activeOnly, p_after_id: after, p_limit: 250,
      });
      if (error) return { data: [], error };
      if (!Array.isArray(data)) throw new Error('The location page response was invalid.');
      const rows = data as LocationRow[];
      locations.push(...rows);
      if (rows.length < 250) break;
      const next = rows[rows.length - 1]?.location_id;
      if (!next || next === after) throw new Error('The location page did not advance.');
      after = next;
    }
  }
  return { data: locations.sort((a, b) => a.name.localeCompare(b.name)), error: null };
}

export async function fetchLocations(
  allowedLocationIds: string[] | null | undefined,
  options?: ClientOption & { activeOnly?: boolean },
): Promise<{ data: LocationRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const filtered = Array.isArray(allowedLocationIds) ? allowedLocationIds.filter(Boolean) : [];
  return revisionedRead(supabase, {}, `locations:${JSON.stringify([filtered.slice().sort(),options?.activeOnly])}`, ['reference'], async () => {
    const { data, error } = await markRequest(supabase.rpc('get_portal_location_choices', {
      p_location_ids: filtered.length ? filtered : null, p_active_only: options?.activeOnly !== false,
    }), 'read');
    return { data: Array.isArray(data) ? (data as LocationRow[]) : [], error: error ?? null };
  });
}

export async function fetchOrderDocSequences(
  locationIds: string[],
  options?: ClientOption,
): Promise<{ data: DocSequenceRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const ids = Array.isArray(locationIds) ? locationIds.filter(Boolean) : [];
  if (!ids.length) return { data: [], error: null };
  try {
    const { data, error } = await supabase
      .from('doc_sequences_effective_v')
      .select('location_id,prefix,doc_type')
      .eq('doc_type', 'ORDER')
      .in('location_id', ids);
    if (error) throw error;
    return { data: Array.isArray(data) ? (data as DocSequenceRow[]) : [], error: null };
  } catch (err) {
    const fallback = await supabase
      .from('doc_sequences')
      .select('location_id,prefix,doc_type')
      .eq('doc_type', 'ORDER')
      .in('location_id', ids);
    return { data: Array.isArray(fallback.data) ? (fallback.data as DocSequenceRow[]) : [], error: fallback.error ?? null };
  }
}

export async function fetchOrderDocSequencePrefixes(
  options?: ClientOption,
): Promise<{ data: DocSequenceRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  try {
    const { data, error } = await supabase
      .from('doc_sequences_effective_v')
      .select('location_id,prefix,doc_type')
      .eq('doc_type', 'ORDER');
    if (error) throw error;
    return { data: Array.isArray(data) ? (data as DocSequenceRow[]) : [], error: null };
  } catch (err) {
    const fallback = await supabase
      .from('doc_sequences')
      .select('location_id,prefix,doc_type')
      .eq('doc_type', 'ORDER');
    return {
      data: Array.isArray(fallback.data) ? (fallback.data as DocSequenceRow[]) : [],
      error: fallback.error ?? null,
    };
  }
}

export async function fetchActiveLocationCount(
  options: ClientOption = {},
): Promise<{ data: number; error: PostgrestError | null }> {
  const supabase = options.supabase ?? getSupabaseClient();
  const { data, error } = await markRequest(supabase.rpc('get_portal_active_location_count'), 'read');
  return { data: Number(data ?? 0), error: error ?? null };
}

export async function fetchLocationNames(
  locationIds: string[],
  options?: ClientOption,
): Promise<{ data: Record<string, string>; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const ids = Array.isArray(locationIds)
    ? locationIds
        .map(id => (typeof id === 'number' ? String(id) : typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean)
    : [];
  if (ids.length === 0) return { data: {}, error: null };
  const { data, error } = await supabase
    .from('locations')
    .select('location_id,name,display_name')
    .in('location_id', ids);
  if (error) return { data: {}, error };
  const map: Record<string, string> = {};
  ids.forEach(id => {
    map[id] = 'Unknown site';
  });
  (Array.isArray(data) ? data : []).forEach(row => {
    const id = row?.location_id != null ? String(row.location_id) : '';
    if (!id) return;
    const labelCandidate = row?.display_name ?? row?.name;
    const normalized = typeof labelCandidate === 'string' ? labelCandidate.trim() : '';
    map[id] = normalized || 'Unnamed site';
  });
  return { data: map, error: null };
}

export const locationNamesQueryKey = (ids: string[]) => ['location-names', ...ids.sort()];

export const getLocationNamesQueryOptions = ({
  locationIds,
  supabase = getSupabaseClient(),
  enabled = true,
}: {
  locationIds: Array<string | null | undefined>;
  supabase?: SupabaseClient;
  enabled?: boolean;
}) => {
  const normalized = locationIds
    .map(id => (typeof id === 'number' ? String(id) : typeof id === 'string' ? id.trim() : ''))
    .filter(Boolean);
  return {
    queryKey: locationNamesQueryKey(normalized),
    enabled: enabled && normalized.length > 0,
    queryFn: async () => {
      const { data, error } = await fetchLocationNames(normalized, { supabase });
      if (error) throw error;
      return data;
    },
  };
};
